import { useEffect } from 'react';
import { useGetInterpretiveReportQuery } from './useGetInterpretiveReportQuery';
import { useGetInterpretiveReportStatusQuery } from './useGetInterpretiveReportStatusQuery';

/**
 * Combined hook that manages interpretive report fetching with generation status polling
 *
 * This hook orchestrates two queries:
 * 1. Status query - Polls for generation progress
 * 2. Report query - Fetches the actual report data
 *
 * When generation completes, it automatically refetches the report.
 *
 * @param surveyId - The survey ID
 * @returns Combined state with report data, status, and loading states
 *
 * @example
 * ```tsx
 * const {
 *   report,
 *   status,
 *   isGenerating,
 *   isComplete,
 *   isFailed,
 *   error,
 *   progress
 * } = useInterpretiveReportWithStatus(surveyId);
 *
 * if (isGenerating) {
 *   return <GenerationProgress progress={progress} step={status?.step} />;
 * }
 *
 * if (isComplete && report) {
 *   return <InterpretiveReportView report={report} />;
 * }
 * ```
 */
const RETRY_STATUS_INTERVAL_MS = 2000;

export const useInterpretiveReportWithStatus = (surveyId: number | undefined) => {
  // Poll for generation status
  const statusQuery = useGetInterpretiveReportStatusQuery(surveyId);

  // Fetch report (initially may not exist if still generating)
  const reportQuery = useGetInterpretiveReportQuery(surveyId);

  const status = statusQuery.data;
  const isGenerating = status?.status === 'IN_PROGRESS' || status?.status === 'PENDING';
  // The report itself is the proof it is done, also before the status has caught up
  const hasReport = !!reportQuery.data;
  const isComplete = status?.status === 'COMPLETED' || hasReport;
  // Fetching the report of a failed generation starts a new one, so it is not failed while that runs
  const isRetrying = status?.status === 'FAILED' && reportQuery.isFetching;
  const isFailed = status?.status === 'FAILED' && !isRetrying && !hasReport;
  const isNotStarted = status?.status === 'NOT_STARTED';

  // When status is NOT_STARTED, trigger generation by fetching the report
  // This handles the race condition where status endpoint is called before report endpoint
  // Not after a failed fetch: every fetch starts a new generation with paid AI calls, so a fast
  // failure would repeat until the status caught up. A new attempt is the user's choice.
  useEffect(() => {
    if (isNotStarted && !reportQuery.data && !reportQuery.isFetching && !reportQuery.isError) {
      reportQuery.refetch();
    }
  }, [isNotStarted, reportQuery]);

  // Status polling stops at FAILED, so follow the new generation until it reports progress again
  useEffect(() => {
    if (!isRetrying) return;
    const timer = setInterval(() => statusQuery.refetch(), RETRY_STATUS_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isRetrying, statusQuery]);

  // When generation completes, refetch the report
  useEffect(() => {
    if (isComplete && !reportQuery.data) {
      reportQuery.refetch();
    }
  }, [isComplete, reportQuery]);

  return {
    // Report data
    report: reportQuery.data,
    reportLoading: reportQuery.isLoading,
    reportError: reportQuery.error,

    // Status data
    status: statusQuery.data,
    statusLoading: statusQuery.isLoading,
    statusError: statusQuery.error,

    // Convenience flags
    isGenerating,
    isComplete,
    isFailed,
    isNotStarted,

    // Progress info
    progress: status?.progress ?? 0,
    step: status?.step ?? null,
    startedAt: status?.startedAt ?? null,
    error: status?.error ?? null,

    // Combined loading state
    isLoading: statusQuery.isLoading || (isGenerating && reportQuery.isLoading),

    // Refetch functions
    refetchReport: reportQuery.refetch,
    refetchStatus: statusQuery.refetch,
  };
};
